/* global VGSShow */
import { useContext, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/system';
// Our Components
import { AuthContext } from 'context/auth-context';
import { CardContext } from 'context/cards/cardContext';
import { REMOVE_FRAME, SHOW_CARD_INFO, ERROR_FRAMES } from 'context/cards/types';
import SnackBarComponent from 'components/shared/SnackBarComponent';
// Utils and Helpers
import { CARD_TYPE } from 'helpers/constants';
// Assets
import copyIcon from './copyIcon.png';
import './PaystandCard.css';

const secretsTextStylesPhysical = {
    '@font-face': {
        'font-family': 'Montserrat',
        src: "url(https://fonts.gstatic.com/s/montserrat/v23/JTUHjIg1_i6t8kCHKm4532VJOt5-QNFgpCtr6Hw5aXx-p7K4KLg.woff) format('woff')",
        'unicode-range':
            'U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD'
    },
    'font-family': 'Montserrat',
    'font-size': '12px',
    'line-height': '12px',
    'letter-spacing': '1px',
    color: '#182d55'
    // display: "block"
};

const secretCardNumberStylesPhysical = {
    ...secretsTextStylesPhysical
};

const secretsTextStylesVirtual = {
    ...secretsTextStylesPhysical,
    color: 'white'
};

const secretCardNumberStylesVirtual = {
    ...secretsTextStylesVirtual
};

const copyButtonStyles = {
    '@font-face': {
        'font-family': 'Montserrat',
        src: "url(https://fonts.gstatic.com/s/montserrat/v23/JTUHjIg1_i6t8kCHKm4532VJOt5-QNFgpCtr6Hw5aXx-p7K4KLg.woff) format('woff')"
    },
    'font-family': 'Montserrat',
    'font-size': '12px',
    cursor: 'pointer',
    'border-radius': '5px',
    height: '20px',
    width: '20px',
    padding: '5px',
    color: 'white',
    background: `url(${copyIcon}) no-repeat center`
};

function addVgsScript(callback) {
    var vgsScript = document.createElement('script');
    vgsScript.id = 'vgs-script';
    vgsScript.src = 'https://js.verygoodvault.com/vgs-show/1.3/ACci3u9aEmutHr7zpAeVY2Zc.js';
    vgsScript.type = 'text/javascript';
    if (callback) vgsScript.onload = callback;
    document.head.append(vgsScript);
}

const PaystandCard = ({ card, showCardInfo, setShowCardInfo }) => {
    const { dispatch, deleteFrame, providerToken } = useContext(CardContext);
    const { businessInfo } = useContext(AuthContext);
    const [vgs, setVgs] = useState({});
    const displaySecretsTimerRef = useRef();
    const cardNumberRef = useRef();
    const cvvRef = useRef();
    const SnackBarRef = useRef();

    useEffect(() => {
        if (card.id && providerToken[card.id]?.id) {
            const displaySecretsTimer = setTimeout(() => {
                if (!vgs['secret-card-number']?.revealed || !vgs['secret-cvv-number']?.revealed) {
                    cvvRef.current.innerHTML = '';
                    cardNumberRef.current.innerHTML = '';
                    dispatch({ type: ERROR_FRAMES });
                }
            }, 20000);
            displaySecretsTimerRef.current = displaySecretsTimer;
            addVgsScript(showCardSecrets);
            return () => clearTimeout(displaySecretsTimer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [card, providerToken]);

    useEffect(() => {
        if (deleteFrame) {
            const VgsIframes = document.querySelectorAll('iframe');
            VgsIframes.forEach(VgsIframe => VgsIframe.remove());
            setVgs({});
            setShowCardInfo(false);
            dispatch({ type: REMOVE_FRAME });
        }
    }, [deleteFrame, dispatch, setShowCardInfo]);

    const showCardSecrets = async () => {
        const vaultId = providerToken[card.id]?.vaultId;
        const vgsShow = VGSShow.create(vaultId, state => {
            setVgs(state);

            if (state['secret-card-number']?.revealed && state['secret-cvv-number']?.revealed) {
                setShowCardInfo(true);
                clearTimeout(displaySecretsTimerRef.current);
                dispatch({
                    type: SHOW_CARD_INFO
                });
            }
        });
        const { cardNumberIframe, cvvIframe } = await fetchCardSecrets(vgsShow);

        const copyCardNumberButton = vgsShow.copyFrom(
            cardNumberIframe,
            {
                serializers: [vgsShow.SERIALIZERS.replace(' ', '')]
            },
            status => {
                if (status === 'success') {
                    SnackBarRef.current.openSnackBar({
                        label: 'Card Number Copied!',
                        severity: 'success'
                    });
                }
            }
        );
        const copyCvvButton = vgsShow.copyFrom(
            cvvIframe,
            {
                serializers: [vgsShow.SERIALIZERS.replace(' ', '')]
            },
            status => {
                if (status === 'success') {
                    SnackBarRef.current.openSnackBar({
                        label: 'CVV Copied!',
                        severity: 'success'
                    });
                }
            }
        );
        renderCardSecrets(cardNumberIframe, cvvIframe);
        renderCopyButtons({
            copyCardNumberButton,
            copyCvvButton
        });
    };
    const fetchCardSecrets = async vgsShow => {
        const vgsShowToken = providerToken[card.id]?.token;
        const request = {
            method: 'GET',
            headers: { 'sd-show-token': vgsShowToken },
            path: `/v1/card/${card.id}/show`
        };
        const cardNumberIframe = vgsShow.request({
            ...request,
            name: 'secret-card-number',
            jsonPathSelector: 'cardNumber',
            serializers: [vgsShow.SERIALIZERS.replace('(\\d{4})(\\d{4})(\\d{4})(\\d{4})', '$1 $2 $3 $4')]
        });
        const cvvIframe = vgsShow.request({
            ...request,
            name: 'secret-cvv-number',
            jsonPathSelector: 'cvv'
        });

        await Promise.all([cardNumberIframe, cvvIframe]);

        return { cardNumberIframe, cvvIframe };
    };
    const renderCardSecrets = (cardNumber, cvv) => {
        const cardNumberRefStyle =
            card.cardType === CARD_TYPE.VIRTUAL ? secretCardNumberStylesVirtual : secretCardNumberStylesPhysical;
        const cvvRefStyle = card.cardType === CARD_TYPE.VIRTUAL ? secretsTextStylesVirtual : secretsTextStylesPhysical;

        cardNumber.render(cardNumberRef.current, cardNumberRefStyle);
        cvv.render(cvvRef.current, cvvRefStyle);
    };
    const renderCopyButtons = ({ copyCardNumberButton, copyCvvButton }) => {
        copyCardNumberButton.render(cardNumberRef.current, copyButtonStyles);
        copyCvvButton.render(cvvRef.current, copyButtonStyles);
    };

    const handleCopyExpDateClick = async () => {
        const expDate = document.querySelector('.request-card-paystand-card__exp-date');
        try {
            await navigator.clipboard.writeText(expDate?.textContent || '');
            SnackBarRef.current?.openSnackBar({ label: 'Expiration Date Copied!', severity: 'success' });
        } catch (error) {
            console.error(error);
            SnackBarRef.current?.openSnackBar({ label: 'Failed to Copy Expiration Date', severity: 'error' });
        }
    };

    return (
        <>
            <Box className="request-card-paystand-card">
                <Box className="request-card-paystand-card__backside" />
                <Box className="request-card-paystand-card__card-number-container">
                    {showCardInfo ? (
                        <Box ref={cardNumberRef} className="request-card-paystand-card__card-number" />
                    ) : (
                        <Box className="request-card-paystand-card__card-number-placeholder">**** **** **** ****</Box>
                    )}
                </Box>
                <Box className="request-card-paystand-card__exp-date-container">
                    <Box className="request-card-paystand-card__exp-date" marginRight="10px">
                        {showCardInfo ? `${card.expiryMonth}/${card.expiryYear.substr(-2)}` : '**/**'}
                    </Box>
                    {showCardInfo && (
                        <img
                            className="request-card-paystand-card__copy-btn"
                            src={copyIcon}
                            alt="Copy exp date button"
                            onClick={handleCopyExpDateClick}
                        />
                    )}
                </Box>
                <Box className="request-card-paystand-card__cvv">{showCardInfo ? '' : '***'}</Box>
                <Box ref={cvvRef} className="request-card-paystand-card__cvv"></Box>

                <Box className="request-card-paystand_card__cardholder-name text-ellipsis">{card.name}</Box>
                <Box className="request-card-paystand_card__business-name">{businessInfo.legal_name}</Box>
            </Box>
            <SnackBarComponent ref={SnackBarRef} />
        </>
    );
};

export default PaystandCard;
